import { IconLoader2 } from "@tabler/icons-react";

import type { CapacityNode } from "../../api/types";
import { shapeLabel, summarizeNode } from "../../lib/capacity";
import { formatElapsed } from "../../lib/format";
import { GpuPills } from "./GpuPills";

interface NodeRowProps {
  node: CapacityNode;
  /** Minutes a new machine takes; `null` when the console cannot say. */
  etaMinutes: number | null;
}

/**
 * One machine: its shape, its name, its GPUs and who holds them.
 *
 * A machine that is still starting is a first-class state, not a greyed
 * anomaly: it is announced as what it is, with how long it has been coming
 * and how long these usually take.
 *
 * Args:
 *     node: The machine as measured.
 *     etaMinutes: How long a machine takes to join, from the API.
 *
 * Returns:
 *     A row for the fleet list.
 */
export function NodeRow({ node, etaMinutes }: NodeRowProps): JSX.Element {
  const starting = node.status === "provisioning";
  // A machine Scaleway is still creating has no name yet, and inventing one
  // would be a lie — so it is announced as what it is.
  const label = node.display_name ?? node.name ?? "New machine";
  return (
    <li className="node-row" data-status={node.status}>
      <div className="node-row__id">
        <span className="node-row__shape">{shapeLabel(node.gpu_total)}</span>
        <span className="mono node-row__name truncate" title={label}>
          {label}
        </span>
        {node.instance_type ? (
          <span className="node-row__type mono">{node.instance_type}</span>
        ) : null}
        {/* The cluster's own name, kept next to the friendly one: it is what
            an operator types into kubectl when something looks wrong. */}
        {node.display_name && node.name ? (
          <span className="node-row__real mono truncate" title={node.name}>
            {node.name}
          </span>
        ) : null}
      </div>

      <GpuPills node={node} />

      <div className="node-row__text">
        {starting ? (
          <p className="node-row__starting">
            <IconLoader2 size={14} aria-hidden="true" className="spin" />
            Starting
            {node.created_at
              ? ` — asked for ${formatElapsed(node.created_at)} ago`
              : " — just asked for"}
            {etaMinutes !== null
              ? `, usually ready in about ${etaMinutes} min.`
              : "."}
          </p>
        ) : (
          <p className="node-row__summary">{summarizeNode(node)}</p>
        )}
      </div>
    </li>
  );
}
